import React from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
  AutoAwesome as AutoAwesomeIcon,
  BookmarkBorder as BookmarkIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Extension as ExtensionIcon,
  Star as StarIcon,
  Bolt as BoltIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  SmartToy as SmartToyIcon,
  Workspaces as WorkspacesIcon,
  Search as SearchIcon,
  Lightbulb as LightbulbIcon,
  Science as ScienceIcon,
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  FolderSpecial as FolderSpecialIcon,
  TravelExplore as TravelExploreIcon,
  Handshake as HandshakeIcon,
  Chat as ChatIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
const DatarateLogo = process.env.PUBLIC_URL + "/datarate_logo.png";

const CHROME_EXTENSION_URL =
  "https://chromewebstore.google.com/detail/datarate/gdjiledfnpkdcjocdiicgcagklenhfdl";
const APP_URL = "https://app.datarate.io";

const BRAND = {
  primary: "rgb(0,202,153)",
  primaryDark: "rgb(0,170,128)",
  primaryLight: "rgba(0,202,153,0.15)",
  gradient: "linear-gradient(135deg, rgb(0,202,153) 0%, rgb(0,170,220) 100%)",
  dark: "#0a0f1c",
  darkCard: "rgba(255,255,255,0.04)",
  darkCardBorder: "rgba(255,255,255,0.08)",
  text: "#ffffff",
  textSecondary: "rgba(255,255,255,0.6)",
};

// Animations
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// Styled Components
const PageWrapper = styled(Box)({
  background: BRAND.dark,
  minHeight: "100vh",
  overflow: "hidden",
  position: "relative",
});

const GlowOrb = styled(Box)(({ top, left, size = 400, color = BRAND.primary }) => ({
  position: "absolute",
  top,
  left,
  width: size,
  height: size,
  borderRadius: "50%",
  background: `radial-gradient(circle, ${color}22 0%, transparent 70%)`,
  filter: "blur(80px)",
  pointerEvents: "none",
  animation: `${pulse} 6s ease-in-out infinite`,
}));

const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4, 2),
  textAlign: "center",
  [theme.breakpoints.down("md")]: {
    minHeight: "auto",
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(8),
  },
}));

const AnimatedBox = styled(Box)(({ delay = 0 }) => ({
  animation: `${fadeInUp} 0.8s ease-out ${delay}s both`,
}));

const GradientText = styled(Typography)({
  background: BRAND.gradient,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  display: "inline",
});

const ShimmerText = styled(Typography)({
  background: `linear-gradient(90deg, ${BRAND.primary}, rgb(0,170,220), ${BRAND.primary})`,
  backgroundSize: "200% auto",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  animation: `${shimmer} 3s linear infinite`,
});

const PrimaryButton = styled(Button)(({ theme }) => ({
  background: BRAND.gradient,
  color: "#fff",
  fontWeight: 700,
  fontSize: "1rem",
  padding: "14px 36px",
  borderRadius: "14px",
  textTransform: "none",
  letterSpacing: "0.5px",
  boxShadow: `0 4px 24px ${BRAND.primary}44`,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 8px 32px ${BRAND.primary}66`,
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: "14px 24px",
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  color: BRAND.primary,
  fontWeight: 600,
  fontSize: "1rem",
  padding: "14px 36px",
  borderRadius: "14px",
  textTransform: "none",
  border: `2px solid ${BRAND.primary}`,
  background: "transparent",
  transition: "all 0.3s ease",
  "&:hover": {
    background: BRAND.primaryLight,
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: "14px 24px",
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  background: BRAND.darkCard,
  backdropFilter: "blur(20px)",
  border: `1px solid ${BRAND.darkCardBorder}`,
  borderRadius: "20px",
  padding: theme.spacing(1),
  height: "100%",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "default",
  "&:hover": {
    transform: "translateY(-8px)",
    border: `1px solid ${BRAND.primary}44`,
    boxShadow: `0 20px 60px ${BRAND.primary}15`,
    background: "rgba(255,255,255,0.06)",
  },
}));

const IconWrapper = styled(Box)({
  width: 56,
  height: 56,
  borderRadius: "16px",
  background: BRAND.primaryLight,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: BRAND.text,
  fontWeight: 800,
  marginBottom: theme.spacing(2),
  lineHeight: 1.2,
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  color: BRAND.textSecondary,
  maxWidth: 640,
  margin: "0 auto",
  marginBottom: theme.spacing(8),
  lineHeight: 1.7,
}));

const StepNumber = styled(Box)({
  width: 48,
  height: 48,
  borderRadius: "50%",
  background: BRAND.gradient,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: "1.2rem",
  color: "#fff",
  marginBottom: 20,
});

const FooterLink = styled(Typography)({
  color: BRAND.textSecondary,
  cursor: "pointer",
  transition: "color 0.2s",
  "&:hover": { color: BRAND.primary },
});

const MiniCube = styled(Box)(({ theme }) => ({
  background: BRAND.darkCard,
  backdropFilter: "blur(20px)",
  border: `1px solid ${BRAND.darkCardBorder}`,
  borderRadius: "16px",
  padding: theme.spacing(3),
  textAlign: "center",
  transition: "all 0.3s ease",
  "&:hover": {
    border: `1px solid ${BRAND.primary}44`,
    background: "rgba(255,255,255,0.06)",
  },
}));

// Data
const features = [
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 28, color: BRAND.primary }} />,
    title: "Smart Tagging",
    desc: "Automatically label and organize what you save with smart tags — so everything stays searchable and structured.",
  },
  {
    icon: <FolderSpecialIcon sx={{ fontSize: 28, color: BRAND.primary }} />,
    title: "Smart Collections",
    desc: "Group related content into collections and datasets for faster access, cleaner workflows, and better organization.",
  },
  {
    icon: <GroupsIcon sx={{ fontSize: 28, color: BRAND.primary }} />,
    title: "Flexible Collaboration",
    desc: "Work solo, with your team, or with the DataRate community. Keep it private, share with selected people, or make it public — your choice.",
  },
  {
    icon: <TravelExploreIcon sx={{ fontSize: 28, color: BRAND.primary }} />,
    title: "AI Agent Search",
    desc: "Need something specific? Let a DataRate AI Agent search and gather relevant sources for your goal — then you decide what's worth saving and rating.",
  },
  {
    icon: <SearchIcon sx={{ fontSize: 28, color: BRAND.primary }} />,
    title: "Smart Search",
    desc: "Find exactly what you need in seconds with fast, intelligent search across your saved content and collections.",
  },
];

const steps = [
  {
    num: "1",
    icon: <StarIcon sx={{ fontSize: 32, color: BRAND.primary }} />,
    title: "Save, Rate, and Organize",
    desc: "Save, rate, and organize websites, links, prompts, AI answers, text, videos, photos, and more via our browser extension or app. Our AI automatically tags and summarizes what you save so that you can find anything fast with intelligent search and structured datasets.",
  },
  {
    num: "2",
    icon: <ShareIcon sx={{ fontSize: 32, color: BRAND.primary }} />,
    title: "Collaborate and Share",
    desc: "Share your best discoveries, build collections, and decide who gets access. Keep it private, share with your team, or make it public. The more people rate, the stronger the trust layer becomes.",
  },
  {
    num: "3",
    icon: <ChatIcon sx={{ fontSize: 32, color: BRAND.primary }} />,
    title: "AI Chat",
    desc: "Use DataRate AI Chat to ask anything and get answers powered by leading AI engines. DataRate then checks the sources behind each answer and shows a clear trust signal. If those sources were already rated, you'll instantly see how reliable the answer is.",
  },
];

const useCases = [
  {
    icon: <BookmarkIcon sx={{ fontSize: 32, color: BRAND.primary }} />,
    title: "Personal Organization",
    desc: "Save articles, bookmarks, and notes in one place. Effortlessly organize content with smart tagging and search features, making it easy to revisit what matters most.",
  },
  {
    icon: <LightbulbIcon sx={{ fontSize: 32, color: BRAND.primary }} />,
    title: "Creative Inspiration",
    desc: "Collect ideas, curate inspiration, and build a personal knowledge base for your projects. Datarate helps you organize and revisit your creativity whenever you need it.",
  },
  {
    icon: <ScienceIcon sx={{ fontSize: 32, color: BRAND.primary }} />,
    title: "Professional Research",
    desc: "Streamline research for work or study. Gather insights, create collections, and access instant summaries to stay efficient and focused on your goals.",
  },
];

const whyChooseItems = [
  "Smart AI-powered organization",
  "Instant content summaries",
  "Personalized recommendations",
  "Secure collaboration features",
  "Cross-platform sync",
  "Community-driven insights",
];

const miniCubes = [
  {
    icon: <LockIcon sx={{ fontSize: 28, color: BRAND.primary }} />,
    title: "Privacy First",
    desc: "Your data stays yours. Control what you share.",
  },
  {
    icon: <BoltIcon sx={{ fontSize: 28, color: BRAND.primary }} />,
    title: "Lightning Fast",
    desc: "Save content with one click.",
  },
  {
    icon: <ScheduleIcon sx={{ fontSize: 28, color: BRAND.primary }} />,
    title: "Save Time",
    desc: "Let AI do the organizing for you.",
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 28, color: BRAND.primary }} />,
    title: "Always Improving",
    desc: "New features added regularly.",
  },
];

const InfoPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <PageWrapper>
      {/* Background Orbs */}
      <GlowOrb top="-10%" left="-5%" size={600} />
      <GlowOrb top="30%" left="80%" size={500} color="rgb(0,170,220)" />
      <GlowOrb top="70%" left="10%" size={450} />

      {/* ──────── Navbar ──────── */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          backdropFilter: "blur(20px)",
          background: "rgba(10,15,28,0.8)",
          borderBottom: `1px solid ${BRAND.darkCardBorder}`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}>
              <img src={DatarateLogo} alt="Datarate" style={{ height: 30, width: 30, objectFit: "contain" }} />
              <Typography
                variant="h6"
                sx={{ color: BRAND.text, fontWeight: 700, letterSpacing: "-0.5px" }}
              >
                Datarate
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              {!isSmall && (
                <Button
                  onClick={() => window.open(APP_URL, "_blank")}
                  sx={{
                    color: BRAND.textSecondary,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { color: BRAND.text },
                  }}
                >
                  Sign In
                </Button>
              )}
              <PrimaryButton
                size="small"
                onClick={() => window.open(CHROME_EXTENSION_URL, "_blank")}
                sx={{ py: 1, px: 3, fontSize: "0.875rem" }}
              >
                <ExtensionIcon sx={{ fontSize: 18, mr: 1 }} />
                Add to Chrome
              </PrimaryButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ──────── Hero ──────── */}
      <HeroSection>
        <Container maxWidth="md">
          <AnimatedBox delay={0}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                background: BRAND.primaryLight,
                borderRadius: "100px",
                px: 2.5,
                py: 0.8,
                mb: 4,
                border: `1px solid ${BRAND.primary}33`,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16, color: BRAND.primary }} />
              <Typography
                variant="caption"
                sx={{ color: BRAND.primary, fontWeight: 600, letterSpacing: "0.5px" }}
              >
                HUMAN-VALIDATED TRUST LAYER
              </Typography>
            </Box>
          </AnimatedBox>

          <AnimatedBox delay={0.1}>
            <Typography
              variant="h1"
              sx={{
                color: BRAND.text,
                fontWeight: 900,
                fontSize: { xs: "2.2rem", sm: "3.2rem", md: "4rem" },
                lineHeight: 1.1,
                mb: 3,
                letterSpacing: "-2px",
              }}
            >
              AI answers you can trust —{" "}
              <GradientText
                variant="h1"
                component="span"
                sx={{
                  fontWeight: 900,
                  fontSize: "inherit",
                  lineHeight: "inherit",
                  letterSpacing: "inherit",
                }}
              >
                powered by human ratings.
              </GradientText>
            </Typography>
          </AnimatedBox>

          <AnimatedBox delay={0.2}>
            <Typography
              variant="h6"
              sx={{
                color: BRAND.textSecondary,
                maxWidth: 600,
                mx: "auto",
                mb: 5,
                lineHeight: 1.7,
                fontWeight: 400,
                fontSize: { xs: "1rem", md: "1.15rem" },
              }}
            >
              We add a human-validated trust layer to every AI answer and piece of web content,
              so your results become more accurate and reliable — every time you save, share,
              or simply rate what you find.
            </Typography>
          </AnimatedBox>

          <AnimatedBox delay={0.3}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
              }}
            >
              <PrimaryButton
                onClick={() => window.open(CHROME_EXTENSION_URL, "_blank")}
                endIcon={<ArrowForwardIcon />}
              >
                Install Free Extension
              </PrimaryButton>
              <SecondaryButton
                onClick={() => window.open(APP_URL, "_blank")}
              >
                Open Web App
              </SecondaryButton>
            </Box>
          </AnimatedBox>

          <AnimatedBox delay={0.5}>
            <Typography
              variant="caption"
              sx={{ color: BRAND.textSecondary, mt: 3, display: "block" }}
            >
              Free to use &middot; No credit card required &middot; Works on Chrome
            </Typography>
          </AnimatedBox>
        </Container>
      </HeroSection>

      {/* ──────── Save, Rate, Organize — 3 Steps ──────── */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          position: "relative",
          background: "rgba(0,202,153,0.02)",
          borderTop: `1px solid ${BRAND.darkCardBorder}`,
          borderBottom: `1px solid ${BRAND.darkCardBorder}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {steps.map((step, i) => (
              <Grid item xs={12} md={4} key={i}>
                <FeatureCard elevation={0}>
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                      <StepNumber>{step.num}</StepNumber>
                      {step.icon}
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ color: BRAND.text, fontWeight: 700, mb: 2 }}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: BRAND.textSecondary, lineHeight: 1.8 }}
                    >
                      {step.desc}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ──────── Knowledge Organization ──────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: "relative" }} id="features">
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <ShimmerText
              variant="overline"
              sx={{ fontWeight: 700, letterSpacing: 3, mb: 2, display: "block" }}
            >
              FEATURES
            </ShimmerText>
            <SectionTitle variant={isMobile ? "h4" : "h3"}>
              Knowledge Organization, Made Simple
            </SectionTitle>
            <SectionSubtitle variant="body1">
              Save, organize, and share trusted information — for personal use, teams, or
              communities. You choose what stays private and what gets shared.
            </SectionSubtitle>
          </Box>

          <Grid container spacing={3}>
            {features.map((f, i) => (
              <Grid item xs={12} sm={6} md={i < 3 ? 4 : 6} key={i}>
                <FeatureCard elevation={0}>
                  <CardContent sx={{ p: 3 }}>
                    <IconWrapper>{f.icon}</IconWrapper>
                    <Typography
                      variant="h6"
                      sx={{ color: BRAND.text, fontWeight: 700, mb: 1.5 }}
                    >
                      {f.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: BRAND.textSecondary, lineHeight: 1.7 }}
                    >
                      {f.desc}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ──────── Use Cases ──────── */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          position: "relative",
          background: "rgba(0,202,153,0.02)",
          borderTop: `1px solid ${BRAND.darkCardBorder}`,
          borderBottom: `1px solid ${BRAND.darkCardBorder}`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <ShimmerText
              variant="overline"
              sx={{ fontWeight: 700, letterSpacing: 3, mb: 2, display: "block" }}
            >
              USE CASES
            </ShimmerText>
            <SectionTitle variant={isMobile ? "h4" : "h3"}>
              Built for Everyone
            </SectionTitle>
            <SectionSubtitle variant="body1">
              Whether you're a student, professional, or creative — Datarate adapts to your workflow.
            </SectionSubtitle>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {useCases.map((uc, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <FeatureCard elevation={0}>
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ mb: 2 }}>{uc.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{ color: BRAND.text, fontWeight: 700, mb: 1.5 }}
                    >
                      {uc.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: BRAND.textSecondary, lineHeight: 1.7 }}
                    >
                      {uc.desc}
                    </Typography>
                  </CardContent>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ──────── Community ──────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: "relative" }}>
        <Container maxWidth="md">
          <Box
            sx={{
              background: `linear-gradient(135deg, ${BRAND.primary}12, rgba(0,170,220,0.08))`,
              border: `1px solid ${BRAND.primary}33`,
              borderRadius: "28px",
              p: { xs: 5, md: 8 },
              position: "relative",
              overflow: "hidden",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${BRAND.primary}22, transparent 70%)`,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -40,
                left: -40,
                width: 160,
                height: 160,
                borderRadius: "50%",
                background: `radial-gradient(circle, rgba(0,170,220,0.15), transparent 70%)`,
              }}
            />

            <ShimmerText
              variant="overline"
              sx={{ fontWeight: 700, letterSpacing: 3, mb: 2, display: "block", position: "relative" }}
            >
              COMMUNITY
            </ShimmerText>
            <Typography
              variant={isMobile ? "h4" : "h3"}
              sx={{
                color: BRAND.text,
                fontWeight: 800,
                mb: 3,
                letterSpacing: "-1px",
                position: "relative",
              }}
            >
              Let's Rate the Web Together!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: BRAND.textSecondary,
                mb: 5,
                maxWidth: 560,
                mx: "auto",
                lineHeight: 1.8,
                position: "relative",
              }}
            >
              Every article you save for the community adds value for others. With Datarate,
              your insights help create a shared pool of knowledge, while you benefit from
              curated recommendations and collective wisdom.
              <br /><br />
              Together, we're building a smarter, more connected way to discover and share.
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                position: "relative",
              }}
            >
              <PrimaryButton
                onClick={() => window.open(APP_URL, "_blank")}
                endIcon={<HandshakeIcon />}
              >
                Collaborate
              </PrimaryButton>
              <SecondaryButton
                onClick={() => window.open(APP_URL, "_blank")}
                endIcon={<GroupsIcon />}
              >
                Share with your team
              </SecondaryButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ──────── Why Choose Datarate ──────── */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          position: "relative",
          background: "rgba(0,202,153,0.02)",
          borderTop: `1px solid ${BRAND.darkCardBorder}`,
          borderBottom: `1px solid ${BRAND.darkCardBorder}`,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <ShimmerText
                variant="overline"
                sx={{ fontWeight: 700, letterSpacing: 3, mb: 2, display: "block" }}
              >
                WHY DATARATE
              </ShimmerText>
              <SectionTitle variant={isMobile ? "h4" : "h3"} sx={{ mb: 3 }}>
                Why Choose Datarate
              </SectionTitle>
              <Typography
                variant="body1"
                sx={{ color: BRAND.textSecondary, lineHeight: 1.8, mb: 4 }}
              >
                Datarate doesn't just help you save content — it empowers you to organize, share,
                and collaborate seamlessly. With features like smart tagging, instant summaries,
                and personalized recommendations, it's more than a tool; it's your partner in
                transforming saved information into actionable insights.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {whyChooseItems.map((item, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 1.5,
                        px: 2,
                        borderRadius: "12px",
                        background: BRAND.darkCard,
                        border: `1px solid ${BRAND.darkCardBorder}`,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          border: `1px solid ${BRAND.primary}44`,
                          background: "rgba(255,255,255,0.06)",
                        },
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 20, color: BRAND.primary, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: BRAND.text, fontWeight: 500 }}>
                        {item}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ──────── Mini Cubes ──────── */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: "relative" }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {miniCubes.map((cube, i) => (
              <Grid item xs={6} md={3} key={i}>
                <MiniCube>
                  <Box sx={{ mb: 2 }}>{cube.icon}</Box>
                  <Typography
                    variant="h6"
                    sx={{ color: BRAND.text, fontWeight: 700, mb: 1, fontSize: "1rem" }}
                  >
                    {cube.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: BRAND.textSecondary, lineHeight: 1.6 }}
                  >
                    {cube.desc}
                  </Typography>
                </MiniCube>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ──────── Final CTA ──────── */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          position: "relative",
          textAlign: "center",
          borderTop: `1px solid ${BRAND.darkCardBorder}`,
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              background: `linear-gradient(135deg, ${BRAND.primary}15, rgba(0,170,220,0.1))`,
              border: `1px solid ${BRAND.primary}33`,
              borderRadius: "28px",
              p: { xs: 5, md: 8 },
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${BRAND.primary}22, transparent 70%)`,
              }}
            />
            <Typography
              variant={isMobile ? "h4" : "h3"}
              sx={{
                color: BRAND.text,
                fontWeight: 800,
                mb: 2,
                letterSpacing: "-1px",
                position: "relative",
              }}
            >
              Ready to get started?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: BRAND.textSecondary,
                mb: 5,
                maxWidth: 480,
                mx: "auto",
                lineHeight: 1.7,
                position: "relative",
              }}
            >
              Join the community and start building your trusted knowledge base today.
            </Typography>
            <PrimaryButton
              onClick={() => window.open(APP_URL, "_blank")}
              endIcon={<ArrowForwardIcon />}
              sx={{ position: "relative", px: 6, py: 2, fontSize: "1.1rem" }}
            >
              Let's Go
            </PrimaryButton>
          </Box>
        </Container>
      </Box>

      {/* ──────── Footer ──────── */}
      <Box
        sx={{
          py: 5,
          borderTop: `1px solid ${BRAND.darkCardBorder}`,
          background: "rgba(0,0,0,0.2)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: { xs: 2, md: 0 } }}>
                <img src={DatarateLogo} alt="Datarate" style={{ height: 26, width: 26, objectFit: "contain" }} />
                <Typography
                  variant="body1"
                  sx={{ color: BRAND.text, fontWeight: 700 }}
                >
                  Datarate
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: BRAND.textSecondary, mt: 1, maxWidth: 280 }}
              >
                AI answers you can trust — powered by human ratings.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", justifyContent: { xs: "flex-start", md: "center" } }}>
                <FooterLink variant="body2" onClick={() => window.open(APP_URL, "_blank")}>
                  Dashboard
                </FooterLink>
                <FooterLink variant="body2" onClick={() => window.open(CHROME_EXTENSION_URL, "_blank")}>
                  Extension
                </FooterLink>
                <FooterLink variant="body2" onClick={() => navigate("/terms")}>
                  Terms
                </FooterLink>
                <FooterLink variant="body2" onClick={() => navigate("/support")}>
                  Support
                </FooterLink>
                <FooterLink variant="body2" onClick={() => navigate("/plans")}>
                  Pricing
                </FooterLink>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{
                  color: BRAND.textSecondary,
                  textAlign: { xs: "left", md: "right" },
                }}
              >
                &copy; {new Date().getFullYear()} Datarate. All rights reserved.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </PageWrapper>
  );
};

export default InfoPage;
